import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, AlertCircle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  useAuth,
  MultiSelectCombobox,
  FilterCombobox,
  SearchInput,
  Button,
  Alert,
  AlertDescription,
  toast,
} from '@aaas/shared';
import { useQueryClient } from '@tanstack/react-query';
import { usePoolListQuery } from '../../hooks/usePoolQueries';
import { useCVListQuery } from '../../hooks/useCVQueries';
import { CVSelectionTable } from './CVSelectionTable';
import { PDFPreviewModal } from '../cv/PDFPreviewModal';
import jobService from '../../services/job.service';
import type { CV } from '../../types';

interface SelectFromPoolModalProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  jobCode: string;
  onSuccess: () => void;
}

export function SelectFromPoolModal({
  open,
  onClose,
  jobId,
  jobCode,
  onSuccess,
}: SelectFromPoolModalProps) {
  const { t } = useTranslation('ats');
  const { user, currentWorkspace, idToken } = useAuth();
  const queryClient = useQueryClient();

  // State
  const [selectedPoolIds, setSelectedPoolIds] = useState<string[]>([]);
  const [selectedCVIds, setSelectedCVIds] = useState<Set<string>>(new Set());
  const [isAdding, setIsAdding] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Pool search and pagination
  const [poolSearchQuery, setPoolSearchQuery] = useState('');
  const [poolPage, setPoolPage] = useState(1);
  const [poolPageSize, setPoolPageSize] = useState(10);

  // CV search and pagination
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(5);

  // PDF preview
  const [previewCV, setPreviewCV] = useState<CV | null>(null);

  // Fetch pools with pagination and search
  const { data: poolsData, isLoading: poolsLoading } = usePoolListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    page: poolPage,
    size: poolPageSize,
    search: poolSearchQuery.trim() || undefined,
    enabled: !!currentWorkspace && open,
  });

  // Fetch CVs from selected pools, or CVs not in any pool if none selected
  const { data: cvsData, isLoading: cvsLoading } = useCVListQuery({
    workspaceId: currentWorkspace?.id,
    idToken: idToken || undefined,
    poolIds: selectedPoolIds.length > 0 ? selectedPoolIds : undefined, // undefined = not in any pool
    processingStatus: statusFilter,
    search: searchQuery.trim() || undefined,
    excludeJobId: jobId, // Backend will query and exclude CVs already in this job
    page,
    size: pageSize,
    enabled: !!currentWorkspace && open,
  });

  const pools = poolsData?.data?.items || [];
  const totalPools = poolsData?.data?.total || 0;
  const totalPoolPages = Math.ceil(totalPools / poolPageSize);

  const cvs = cvsData?.data?.items || [];
  const totalCVs = cvsData?.data?.total || 0;
  const totalPages = Math.ceil(totalCVs / pageSize);

  // Processing status options for filter
  const statusOptions = [
    { value: 'processed', label: t('selectFromPool.statusOptions.processed') },
    { value: 'processing', label: t('selectFromPool.statusOptions.processing') },
    { value: 'pending', label: t('selectFromPool.statusOptions.pending') },
    { value: 'failed', label: t('selectFromPool.statusOptions.failed') },
  ];

  // Toggle CV selection
  const toggleSelect = useCallback((cvId: string) => {
    setSelectedCVIds((prev) => {
      const next = new Set(prev);
      if (next.has(cvId)) {
        next.delete(cvId);
      } else {
        next.add(cvId);
      }
      return next;
    });
  }, []);

  // Handle CV preview
  const handlePreviewCV = useCallback((cv: CV) => {
    setPreviewCV(cv);
  }, []);

  // Add CVs to job
  const handleAddToJob = async () => {
    if (!user || !currentWorkspace || selectedCVIds.size === 0) return;

    setIsAdding(true);
    setError(null);

    try {
      const token = await user.getIdToken();
      const result = await jobService.addExistingCVsToJob(
        jobId,
        currentWorkspace.id,
        token,
        Array.from(selectedCVIds)
      );

      // Show success/warning message if some CVs were skipped
      if (result.skipped && result.skipped.length > 0) {
        const addedCount = selectedCVIds.size - result.skipped.length;
        toast.warning(t('selectFromPool.partialAdded', { added: addedCount, skipped: result.skipped.length }));
      } else {
        toast.success(t('selectFromPool.addedSuccess', { count: selectedCVIds.size }));
      }

      // Invalidate job kanban to show new CVs
      queryClient.invalidateQueries({ queryKey: ['job-kanban', jobId, currentWorkspace.id] });

      // Reset state
      setSelectedPoolIds([]);
      setSelectedCVIds(new Set());

      onSuccess();
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('selectFromPool.addFailed');
      setError(errorMessage);
    } finally {
      setIsAdding(false);
    }
  };

  // Pool search handler
  const handlePoolSearchChange = (value: string) => {
    setPoolSearchQuery(value);
    setPoolPage(1);
  };

  // Reset page when filters change
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setPage(1);
  };

  const handleStatusFilterChange = (value: string | undefined) => {
    setStatusFilter(value);
    setPage(1);
  };

  const handlePoolsChange = (pools: string[]) => {
    setSelectedPoolIds(pools);
    setPage(1);
    setSelectedCVIds(new Set()); // Clear selections when pools change
  };

  // Reset state when modal closes
  const handleClose = () => {
    setSelectedPoolIds([]);
    setSelectedCVIds(new Set());
    setPoolSearchQuery('');
    setPoolPage(1);
    setPoolPageSize(10);
    setSearchQuery('');
    setStatusFilter(undefined);
    setPage(1);
    setPageSize(5);
    setError(null);
    onClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col p-4 sm:p-6">
          <DialogHeader>
            <DialogTitle className="text-base sm:text-lg">
              {t('selectFromPool.title', { jobCode })}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4 flex-1 flex flex-col min-h-0">
            {/* Pool selector */}
            <div className="space-y-2">
              <label className="block text-xs sm:text-sm font-medium text-gray-700">
                {t('selectFromPool.selectPoolsLabel')} <span className="text-gray-500 font-normal">{t('selectFromPool.selectPoolsHint')}</span>
              </label>

              {/* Pool multi-select with integrated search */}
              <MultiSelectCombobox
                options={pools.map((pool) => ({ value: pool.id, label: pool.name }))}
                values={selectedPoolIds}
                onChange={handlePoolsChange}
                placeholder={t('selectFromPool.selectPoolsPlaceholder')}
                displayName={t('selectFromPool.poolsName')}
                searchPlaceholder={t('selectFromPool.searchPoolsPlaceholder')}
                emptyMessage={poolsLoading ? t('common.loading') : t('selectFromPool.noPoolsFound')}
                isLoading={poolsLoading}
                className="w-full h-10"
                onSearchChange={handlePoolSearchChange}
                searchValue={poolSearchQuery}
              />

              {/* Pool pagination */}
              {totalPoolPages > 1 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 pt-2 text-xs text-gray-600">
                  <div className="flex items-center gap-2">
                    <span>
                      {t('selectFromPool.pageStatus', { page: poolPage, total: totalPoolPages })}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="hidden sm:inline">{t('selectFromPool.poolsTotal', { count: totalPools })}</span>
                  </div>

                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    {/* Page size selector */}
                    <select
                      value={poolPageSize}
                      onChange={(e) => {
                        setPoolPageSize(Number(e.target.value));
                        setPoolPage(1);
                      }}
                      className="text-xs border border-gray-300 rounded px-2 py-1 flex-1 sm:flex-initial"
                    >
                      <option value={10}>{t('selectFromPool.pageSize', { count: 10 })}</option>
                      <option value={20}>{t('selectFromPool.pageSize', { count: 20 })}</option>
                      <option value={50}>{t('selectFromPool.pageSize', { count: 50 })}</option>
                    </select>

                    {/* Page navigation */}
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPoolPage((p) => Math.max(1, p - 1))}
                        disabled={poolPage === 1}
                        className="h-7 w-7 p-0"
                      >
                        <ChevronLeft className="h-3 w-3" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPoolPage((p) => Math.min(totalPoolPages, p + 1))}
                        disabled={poolPage === totalPoolPages}
                        className="h-7 w-7 p-0"
                      >
                        <ChevronRight className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Search and Filter - Always visible */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4">
              {/* Search input */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                  {t('selectFromPool.searchLabel')}
                </label>
                <SearchInput
                  placeholder={t('selectFromPool.searchPlaceholder')}
                  onSearch={handleSearchChange}
                  initialValue={searchQuery}
                  width="xl"
                  className="w-full"
                  showClearButton={true}
                />
              </div>

              {/* Status filter */}
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1.5 sm:mb-2">
                  {t('selectFromPool.statusLabel')}
                </label>
                <div className="h-10">
                  <FilterCombobox
                    options={statusOptions}
                    value={statusFilter}
                    onChange={handleStatusFilterChange}
                    placeholder={t('selectFromPool.allStatuses')}
                    showClearOption={true}
                    className='h-10'
                  />
                </div>
              </div>
            </div>

            {/* Error alert */}
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            {/* CV table - Always visible with scroll */}
            <CVSelectionTable
              cvs={cvs}
              selectedCVIds={selectedCVIds}
              onToggleSelect={toggleSelect}
              isLoading={cvsLoading}
              onPreviewCV={handlePreviewCV}
            />

            {/* Controls section - Fixed at bottom */}
            <div className="space-y-3 flex-shrink-0">
              {/* Pagination controls - hidden when no CVs */}
              {totalCVs > 0 && (
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 py-3 border-t border-gray-200">
                  <div className="flex items-center gap-2 text-xs sm:text-sm text-gray-600">
                    <span>
                      {t('selectFromPool.pageStatus', { page, total: totalPages })}
                    </span>
                    <span className="hidden sm:inline">•</span>
                    <span className="hidden sm:inline">{t('selectFromPool.cvsTotal', { count: totalCVs })}</span>
                  </div>

                  <div className="flex items-center gap-2 w-full sm:w-auto">
                    {/* Page size selector */}
                    <select
                      value={pageSize}
                      onChange={(e) => {
                        setPageSize(Number(e.target.value));
                        setPage(1);
                      }}
                      className="text-xs sm:text-sm border border-gray-300 rounded px-2 py-1.5 flex-1 sm:flex-initial min-h-[38px]"
                    >
                      <option value={5}>{t('selectFromPool.pageSize', { count: 5 })}</option>
                      <option value={10}>{t('selectFromPool.pageSize', { count: 10 })}</option>
                      <option value={20}>{t('selectFromPool.pageSize', { count: 20 })}</option>
                    </select>

                    {/* Page navigation */}
                    <div className="flex gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="h-9 w-9 p-0 sm:h-8 sm:w-8"
                      >
                        <ChevronLeft className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="h-9 w-9 p-0 sm:h-8 sm:w-8"
                      >
                        <ChevronRight className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* Selection summary and action buttons */}
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-center gap-2 text-xs sm:text-sm text-gray-600">
                  <span>{t('selectFromPool.showing', { count: cvs.length })}</span>
                  {totalCVs > cvs.length && (
                    <>
                      <span className="hidden sm:inline">•</span>
                      <span className="text-xs">
                        ({cvs.length} / {totalCVs})
                      </span>
                    </>
                  )}
                  <span>•</span>
                  <span className="text-orange-600 font-medium">{t('selectFromPool.selected', { count: selectedCVIds.size })}</span>
                </div>

                {/* Action buttons */}
                <div className="flex gap-2 w-full sm:w-auto">
                  <Button
                    variant="outline"
                    onClick={handleClose}
                    disabled={isAdding}
                    className="flex-1 sm:flex-initial min-h-[38px]"
                  >
                    {t('selectFromPool.cancel')}
                  </Button>
                  <Button
                    onClick={handleAddToJob}
                    disabled={selectedCVIds.size === 0 || isAdding}
                    className="flex-1 sm:flex-initial min-h-[38px]"
                  >
                    {isAdding ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        {t('selectFromPool.adding')}
                      </>
                    ) : (
                      t('selectFromPool.addCvs', { count: selectedCVIds.size })
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Modal */}
      {previewCV && previewCV.attachments.length > 0 && (
        <PDFPreviewModal
          open={!!previewCV}
          onClose={() => setPreviewCV(null)}
          cvId={previewCV.id}
          attachmentId={previewCV.attachments[0].id}
          fileName={previewCV.attachments[0].file_name}
        />
      )}
    </>
  );
}
